/**
 * 로그인 실패 안내 문구.
 * 백엔드 스펙: smartbugo-api/docs/login-device-lock.md
 *
 * 로그인 응답의 실패 갈래가 셋으로 늘었다.
 *   401 + LOGIN_REJECTED     비밀번호 불일치 (남은 횟수가 실려 온다)
 *   429 + LOGIN_DEVICE_LOCKED 이 기기가 잠김 (비밀번호가 맞아도 429)
 *   403                       번호 미등록 운영 계정 등 — 서버 문장을 그대로 쓴다
 *
 * 남은 횟수·잠금 시각은 잠금 기능이 켜져 있고 기기 ID 헤더가 실렸을 때만 온다.
 * 구서버·헤더 없는 요청에서는 필드가 없으므로 항상 없을 수 있다고 보고 쓴다.
 */

import { storeLoginDeviceLock } from '@shared/utils/loginDevice.util';

export const LOGIN_REJECTED = 'LOGIN_REJECTED';
export const LOGIN_DEVICE_LOCKED = 'LOGIN_DEVICE_LOCKED';
/** 403 code — 등록된 휴대폰번호가 없어 본인 인증을 진행할 수 없는 운영 계정 */
export const PHONE_NOT_REGISTERED = 'PHONE_NOT_REGISTERED';

export interface LoginErrorBody {
  type?: string;
  code?: string;
  message?: string;
  /** 429 */
  lockedUntil?: string;
  retryAfterSec?: number;
  /** 401 */
  failCount?: number;
  maxFail?: number;
  attemptsLeft?: number;
}

const bodyOf = (err: unknown): LoginErrorBody =>
  ((err as any)?.response?.data as LoginErrorBody) ?? {};

const statusOf = (err: unknown): number | undefined =>
  (err as any)?.response?.status;

/** "오후 3시 40분" 처럼 사람이 읽는 시각 */
const clockText = (value?: string): string | null => {
  if (!value) return null;
  const at = new Date(value);
  if (Number.isNaN(at.getTime())) return null;
  return at.toLocaleTimeString('ko-KR', {
    hour: 'numeric',
    minute: '2-digit',
  });
};

/** 남은 잠금 시간을 "약 32분" 으로 (초 단위는 보여줘야 의미가 없다) */
const remainText = (retryAfterSec?: number): string | null => {
  if (typeof retryAfterSec !== 'number' || retryAfterSec <= 0) return null;
  const minutes = Math.ceil(retryAfterSec / 60);
  return minutes >= 60
    ? `약 ${Math.ceil(minutes / 60)}시간`
    : `약 ${minutes}분`;
};

/** 이 기기가 잠긴 응답인가 (429) */
export const isLoginDeviceLocked = (err: unknown): boolean =>
  statusOf(err) === 429 || bodyOf(err).type === LOGIN_DEVICE_LOCKED;

/** 잠금 안내 문구. 남은 시간·해제 시각을 알 수 있으면 함께 */
export const loginLockMessage = (err: unknown): string => {
  const body = bodyOf(err);
  const base =
    body.message ?? '로그인 시도가 많아 잠시 로그인할 수 없습니다.';
  const until = clockText(body.lockedUntil);
  const remain = remainText(body.retryAfterSec);
  if (until && remain) return `${base} ${until} 이후(${remain}) 다시 시도해 주세요.`;
  if (until) return `${base} ${until} 이후 다시 시도해 주세요.`;
  if (remain) return `${base} ${remain} 뒤에 다시 시도해 주세요.`;
  return `${base} 계속 막히면 관리자에게 문의해 주세요.`;
};

/**
 * 로그인 실패 안내 문구 한 줄.
 *
 * 서버 message 를 그대로 쓰는 건 403 뿐이다 — 401 의 message 는
 * `user=admin not exist or password incorrect` 라는 내부 문장이라 사용자에게 보이면 안 된다.
 */
export const loginErrorMessage = (err: unknown): string => {
  if (isLoginDeviceLocked(err)) return loginLockMessage(err);

  const status = statusOf(err);
  const body = bodyOf(err);

  if (status === 401) {
    const base = '아이디 혹은 비밀번호(인증정보)를 잘못 입력하셨습니다.';
    //* 남은 횟수는 잠금 대상 계정에만 실려 온다 (고객 계정은 세지 않는다)
    if (typeof body.attemptsLeft === 'number' && body.attemptsLeft > 0) {
      return `${base} ${body.attemptsLeft}회 더 틀리면 이 기기에서 로그인이 잠깁니다.`;
    }
    return base;
  }

  //* 403 은 서버가 사유를 문장으로 준다 (번호 미등록 운영 계정 등)
  if (status === 403 && typeof body.message === 'string' && body.message) {
    return body.message;
  }
  if (status === 403) return '해당 사이트에 접속할 권한이 없는 회원입니다.';

  return '알 수 없는 에러가 발생했습니다. 지속된다면 고객센터에 문의해주시기 바랍니다.';
};

/**
 * "현재 비밀번호 확인" 실패 문구.
 *
 * 이 확인은 로그인 엔드포인트를 다시 호출하므로 **기기 잠금 횟수에 그대로 쌓인다.**
 * 비밀번호 변경 화면에서 5번 틀린 관리자는 로그인까지 잠긴다 — 남은 횟수를 반드시 보여줘야 한다.
 */
export const passwordCheckErrorMessage = (err: unknown): string => {
  if (isLoginDeviceLocked(err)) return loginLockMessage(err);

  const base = '현재 비밀번호가 일치하지 않습니다.';
  const { attemptsLeft } = bodyOf(err);
  if (typeof attemptsLeft === 'number' && attemptsLeft > 0) {
    return `${base} ${attemptsLeft}회 더 틀리면 이 기기에서 로그인이 잠깁니다.`;
  }
  return base;
};

// ---- 로그인 화면용: 실패 응답 → 화면 상태 ------------------------------------------

/** 서버 기본값(관리자급 5회·60분 / 작업자급 10회·30분). maxFail 로 잠금 시간을 역추정한다 */
export const lockMinutesOf = (maxFail?: number): number =>
  typeof maxFail === 'number' && maxFail >= 10 ? 30 : 60;

export type LoginFailure =
  /** 429 — 이 기기가 잠김 */
  | { kind: 'locked'; lockedUntil: string; message?: string }
  /** 403 PHONE_NOT_REGISTERED — 비밀번호는 맞았지만 번호가 없어 본인 인증을 할 수 없다 */
  | { kind: 'phone-not-registered'; message?: string }
  /** 401 — 불일치. 잠금 대상 계정이면 서버가 횟수를 같이 준다 (고객 계정은 없다) */
  | { kind: 'rejected'; failCount?: number; maxFail?: number; attemptsLeft?: number }
  /** 그 외 (네트워크·서버 오류·권한 없음 등) */
  | { kind: 'other'; message?: string };

const asNumber = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined;

/**
 * 로그인 호출의 에러를 로그인 화면 상태로 바꾼다.
 * 잠김이면 localStorage 캐시도 갱신한다 (새로고침 뒤에도 카운트다운 유지).
 *
 * 남은 횟수는 서버가 준 값만 쓴다. 로그인 폼을 고객(잠금 대상 아님)과 직원이 같이 쓰므로
 * 프론트에서 따로 세면 고객에게 없는 잠금을 경고하게 된다.
 */
export const parseLoginFailure = (err: unknown): LoginFailure => {
  const status = statusOf(err);
  const body = bodyOf(err);

  if (isLoginDeviceLocked(err)) {
    const lockedUntil =
      typeof body.lockedUntil === 'string' && Number.isFinite(Date.parse(body.lockedUntil))
        ? body.lockedUntil
        : new Date(
            Date.now() + Math.max(60, body.retryAfterSec ?? 30 * 60) * 1000,
          ).toISOString();
    storeLoginDeviceLock({ lockedUntil });
    return { kind: 'locked', lockedUntil, message: body.message };
  }

  //* 번호 미등록은 "비밀번호가 틀렸다"가 아니다 — 횟수 안내 대신 관리자 문의로 보낸다.
  //* code 는 예외 필터가 살려 보내지만, 구서버 대비 403+문장도 같은 갈래로 본다
  if (body.code === PHONE_NOT_REGISTERED || (status === 403 && body.message)) {
    return { kind: 'phone-not-registered', message: body.message };
  }

  if (status === 401) {
    return {
      kind: 'rejected',
      failCount: asNumber(body.failCount),
      maxFail: asNumber(body.maxFail),
      attemptsLeft: asNumber(body.attemptsLeft),
    };
  }

  return {
    kind: 'other',
    message: status === 403 ? '해당 사이트에 접속할 권한이 없는 회원입니다.' : undefined,
  };
};

// ---- 비밀번호 설정 실패 ----------------------------------------------------------

export const PASSWORD_TOO_WEAK = 'PASSWORD_TOO_WEAK';

/**
 * 새 비밀번호 설정(본인 변경·관리자 생성·관리자 수정) 실패 문구.
 *
 * 취약 비밀번호는 400 `type: PASSWORD_TOO_WEAK` 로 오고, 기준이 **계정 등급별**이라
 * 프론트가 규칙을 복제하지 않는다 — 서버 message 를 그대로 띄운다. 그 외는 fallback.
 */
export const passwordSetErrorMessage = (err: unknown, fallback: string): string => {
  const body = bodyOf(err);
  if (
    statusOf(err) === 400 &&
    body.type === PASSWORD_TOO_WEAK &&
    typeof body.message === 'string' &&
    body.message
  ) {
    return body.message;
  }
  return fallback;
};
